import React, { useState, useRef } from 'react';
import { Upload, X, Loader, RefreshCw, Wand2, Crop } from 'lucide-react';
// MIGRATION FIREBASE COMPLETE: Plus de backend Python
import { uploadService } from '../services/firebaseService';
import { removeBackground as removeBackgroundCF } from '../services/cloudFunctions';  // Cloud Function pour remove.bg
import { useToast } from './Toast';
import ImageCropModal from './ImageCropModal';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { app } from '../firebase/config';
// heic2any remplacé par Cloud Function convertHeicToJpeg (meilleur support iPhone)

export const ImageUpload = ({
  currentImage,
  onImageChange,
  label = "Image",
  folder = "images",
  showRemoveBackground = true,  // Afficher l'option par défaut
  showCrop = true,  // Afficher l'option de recadrage
  aspectRatio = 1   // Ratio par défaut (1 = carré)
}) => {
  const toast = useToast();
  const [uploading, setUploading] = useState(false);
  const [removingBg, setRemovingBg] = useState(false);
  const [preview, setPreview] = useState(currentImage || null);
  const [removeBackground, setRemoveBackground] = useState(false);
  const fileInputRef = useRef(null);

  // États pour le crop
  const [showCropModal, setShowCropModal] = useState(false);
  const [imageToCrop, setImageToCrop] = useState(null);
  const [pendingFile, setPendingFile] = useState(null);
  const [loadingCrop, setLoadingCrop] = useState(false);

  // Convertir une URL en blob local via Cloud Function (bypass CORS pour le canvas)
  const fetchImageAsBlob = async (imageUrl) => {
    try {
      // CLOUD FUNCTION: proxyImage retourne l'image en base64
      const functions = getFunctions(app, 'europe-west1');
      const proxyImage = httpsCallable(functions, 'proxyImage');
      const result = await proxyImage({ imageUrl });

      // Convertir dataUrl en blob URL
      const response = await fetch(result.data.dataUrl);
      const blob = await response.blob();
      return URL.createObjectURL(blob);
    } catch (error) {
      console.error('Erreur fetch image via Cloud Function:', error);
      return null;
    }
  };

  const handleFileChange = async (e) => {
    let file = e.target.files[0];
    if (!file) return;

    // Vérifier que c'est une image (inclut HEIC/HEIF)
    const isImage = file.type.startsWith('image/');
    const isHeic = file.name.toLowerCase().endsWith('.heic') || file.name.toLowerCase().endsWith('.heif');
    if (!isImage && !isHeic) {
      toast.error('Erreur', 'Veuillez sélectionner une image');
      return;
    }

    // Convertir HEIC en JPEG si nécessaire (via Cloud Function pour meilleur support)
    if (isHeic) {
      try {
        console.log('[ImageUpload] 🔄 Conversion HEIC → JPEG (Cloud Function)...', {
          name: file.name,
          size: file.size,
          type: file.type
        });
        setUploading(true);

        // Vérifier la taille (max 20MB pour éviter timeout)
        if (file.size > 20 * 1024 * 1024) {
          throw new Error('Fichier trop volumineux (max 20MB)');
        }

        // Lire le fichier en base64
        const base64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });

        // Appeler la Cloud Function
        const functions = getFunctions(app, 'europe-west1');
        const convertHeic = httpsCallable(functions, 'convertHeicToJpeg');
        const result = await convertHeic({ imageBase64: base64, fileName: file.name });

        if (!result.data.success) {
          throw new Error('Conversion échouée');
        }

        // Convertir le résultat base64 en File
        const response = await fetch(result.data.jpegBase64);
        const blob = await response.blob();
        file = new File([blob], file.name.replace(/\.heic$/i, '.jpg').replace(/\.heif$/i, '.jpg'), { type: 'image/jpeg' });

        console.log('[ImageUpload] ✅ Conversion HEIC réussie:', file.size, 'bytes');
        setUploading(false);
      } catch (err) {
        console.error('[ImageUpload] ❌ Erreur conversion HEIC:', err);
        const errorMsg = err.message || 'Erreur de conversion HEIC';
        toast.error('Erreur HEIC', errorMsg);
        setUploading(false);
        return;
      }
    }

    // Si crop activé, ouvrir le modal de recadrage
    if (showCrop) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImageToCrop(reader.result);
        setPendingFile(file);
        setShowCropModal(true);
      };
      reader.readAsDataURL(file);
      return;
    }

    // Sinon, upload direct (comportement original)
    await uploadImage(file);
  };

  // Upload d'une image (fichier ou blob)
  const uploadImage = async (fileOrBlob) => {
    // Preview local si c'est un fichier
    if (fileOrBlob instanceof File) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result);
      };
      reader.readAsDataURL(fileOrBlob);
    }

    // FIREBASE DIRECT: Upload vers Firebase Storage
    setUploading(true);
    try {
      const result = await uploadService.uploadImage(fileOrBlob, folder);
      let imageUrl = result.url;

      // Si checkbox cochée, supprimer le fond
      if (removeBackground) {
        setUploading(false);
        setRemovingBg(true);
        try {
          const bgResponse = await removeBackgroundCF(imageUrl);
          imageUrl = bgResponse.url;
          toast.success('Succès', 'Image uploadée et fond supprimé !');
        } catch (bgError) {
          console.error('Erreur suppression fond:', bgError);
          toast.warning('Attention', 'Image uploadée mais échec suppression du fond');
        }
      } else {
        toast.success('Succès', 'Image uploadée !');
      }

      onImageChange(imageUrl);
      setPreview(imageUrl);
    } catch (error) {
      console.error('Erreur upload:', error);
      toast.error('Erreur', 'Erreur lors de l\'upload de l\'image');
      setPreview(currentImage);
    } finally {
      setUploading(false);
      setRemovingBg(false);
    }
  };

  // Callback après crop
  const handleCropComplete = async (croppedBlob) => {
    // Libérer la mémoire du blob URL si présent
    if (imageToCrop && imageToCrop.startsWith('blob:')) {
      URL.revokeObjectURL(imageToCrop);
    }
    setShowCropModal(false);
    setImageToCrop(null);
    setPendingFile(null);
    await uploadImage(croppedBlob);
  };

  // Ouvrir le crop pour une image existante
  const handleCropExisting = async () => {
    if (!preview || typeof preview !== 'string') {
      toast.error('Erreur', 'Aucune image à recadrer');
      return;
    }

    // Si c'est une data URL, on peut l'utiliser directement
    if (preview.startsWith('data:')) {
      setImageToCrop(preview);
      setShowCropModal(true);
      return;
    }

    // Pour les URLs externes (Firebase), télécharger en blob pour bypass CORS
    setLoadingCrop(true);
    try {
      const blobUrl = await fetchImageAsBlob(preview);
      if (blobUrl) {
        setImageToCrop(blobUrl);
        setShowCropModal(true);
      } else {
        toast.error('Erreur', 'Impossible de charger l\'image pour le recadrage');
      }
    } catch (error) {
      console.error('Erreur chargement image:', error);
      toast.error('Erreur', 'Impossible de charger l\'image');
    } finally {
      setLoadingCrop(false);
    }
  };

  const handleRemove = () => {
    setPreview(null);
    onImageChange('');
  };

  // Supprimer le fond d'une image existante
  const handleRemoveBackgroundExisting = async () => {
    if (!preview || typeof preview !== 'string' || preview.startsWith('data:')) {
      toast.error('Erreur', 'Aucune image à traiter');
      return;
    }

    setRemovingBg(true);
    try {
      const response = await removeBackgroundCF(preview);
      const newUrl = response.url;
      setPreview(newUrl);
      onImageChange(newUrl);
      toast.success('Succès', 'Fond supprimé !');
    } catch (error) {
      console.error('Erreur suppression fond:', error);
      // Message user-friendly selon le type d'erreur (Cloud Function)
      const errorMessage = error.message || '';

      if (errorMessage.includes('unavailable') || errorMessage.includes('API key')) {
        toast.warning('Service indisponible', 'La suppression de fond est temporairement indisponible.');
      } else {
        toast.error('Erreur', 'Échec de la suppression du fond');
      }
    } finally {
      setRemovingBg(false);
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  // Sync preview with currentImage prop
  React.useEffect(() => {
    setPreview(currentImage || null);
  }, [currentImage]);

  return (
    <div>
      <label className="block text-sm font-bold text-gray-700 mb-2">{label}</label>

      {/* Input file caché - utilisé par les deux états */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*,.heic,.heif,image/heic,image/heif"
        onChange={handleFileChange}
        className="hidden"
        disabled={uploading}
      />

      {preview ? (
        <div className="relative">
          <img
            src={preview}
            alt="Preview"
            className="w-full h-48 object-cover rounded-lg border-2 border-gray-300"
          />

          {/* Overlay de chargement */}
          {(uploading || removingBg) && (
            <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center rounded-lg">
              <div className="flex flex-col items-center text-white">
                <Loader className="w-10 h-10 animate-spin mb-2" />
                <p className="text-sm">
                  {removingBg ? 'Suppression du fond...' : 'Upload en cours...'}
                </p>
              </div>
            </div>
          )}

          {/* Boutons d'action sur l'image */}
          <div className="absolute top-2 right-2 flex gap-2">
            {/* Bouton Recadrer */}
            {showCrop && (
              <button
                type="button"
                onClick={handleCropExisting}
                disabled={uploading || removingBg || loadingCrop}
                className="p-2 bg-orange-500 text-white rounded-full hover:bg-orange-600 transition-colors shadow-lg disabled:opacity-50"
                title="Recadrer l'image"
              >
                {loadingCrop ? <Loader className="w-4 h-4 animate-spin" /> : <Crop className="w-4 h-4" />}
              </button>
            )}

            {/* Bouton Supprimer le fond */}
            {showRemoveBackground && (
              <button
                type="button"
                onClick={handleRemoveBackgroundExisting}
                disabled={uploading || removingBg}
                className="p-2 bg-purple-500 text-white rounded-full hover:bg-purple-600 transition-colors shadow-lg disabled:opacity-50"
                title="Supprimer le fond"
              >
                <Wand2 className="w-4 h-4" />
              </button>
            )}

            {/* Bouton Changer l'image */}
            <button
              type="button"
              onClick={triggerFileInput}
              disabled={uploading || removingBg}
              className="p-2 bg-blue-500 text-white rounded-full hover:bg-blue-600 transition-colors shadow-lg disabled:opacity-50"
              title="Changer l'image"
            >
              <RefreshCw className="w-4 h-4" />
            </button>

            {/* Bouton Supprimer */}
            <button
              type="button"
              onClick={handleRemove}
              disabled={uploading || removingBg}
              className="p-2 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors shadow-lg disabled:opacity-50"
              title="Supprimer l'image"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Bande en bas pour changer facilement */}
          <div
            onClick={triggerFileInput}
            className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-60 text-white py-2 px-4 rounded-b-lg cursor-pointer hover:bg-opacity-80 transition-all flex items-center justify-center gap-2"
          >
            <Upload className="w-4 h-4" />
            <span className="text-sm font-medium">Changer l'image</span>
          </div>
        </div>
      ) : (
        <div
          onClick={triggerFileInput}
          className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-primary hover:bg-gray-50 transition-colors cursor-pointer"
        >
          {uploading ? (
            <div className="flex flex-col items-center">
              <Loader className="w-12 h-12 text-primary animate-spin mb-2" />
              <p className="text-sm text-gray-600">Upload en cours...</p>
            </div>
          ) : (
            <div className="flex flex-col items-center">
              <Upload className="w-12 h-12 text-gray-400 mb-2" />
              <p className="text-sm text-gray-600 mb-1">Cliquez pour uploader une image</p>
              <p className="text-xs text-gray-400">PNG, JPG, WEBP jusqu'a 10MB</p>
            </div>
          )}
        </div>
      )}

      {/* Checkbox supprimer le fond - s'affiche uniquement si pas d'image */}
      {showRemoveBackground && !preview && (
        <div className="mt-3 flex items-center gap-2">
          <input
            type="checkbox"
            id="removeBackground"
            checked={removeBackground}
            onChange={(e) => setRemoveBackground(e.target.checked)}
            className="w-4 h-4 text-purple-600 rounded"
          />
          <label htmlFor="removeBackground" className="text-sm text-gray-700 flex items-center gap-1">
            <Wand2 className="w-4 h-4 text-purple-500" />
            Supprimer le fond automatiquement
          </label>
        </div>
      )}

      {/* URL manuelle (optionnel) */}
      <div className="mt-3">
        <input
          type="text"
          value={typeof preview === 'string' && !preview.startsWith('data:') ? preview : ''}
          onChange={(e) => {
            setPreview(e.target.value);
            onImageChange(e.target.value);
          }}
          placeholder="Ou collez une URL d'image..."
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
        />
      </div>

      {/* Modal de recadrage */}
      <ImageCropModal
        isOpen={showCropModal}
        onClose={() => {
          // Libérer la mémoire du blob URL si présent
          if (imageToCrop && imageToCrop.startsWith('blob:')) {
            URL.revokeObjectURL(imageToCrop);
          }
          setShowCropModal(false);
          setImageToCrop(null);
          setPendingFile(null);
        }}
        imageSrc={imageToCrop}
        onCropComplete={handleCropComplete}
        aspectRatio={aspectRatio}
        title="Recadrer l'image"
      />
    </div>
  );
};
